import React, { useState, useEffect, useRef } from "react";
import {
  Grid,
  Rating,
  TextField,
  FormControlLabel,
  Switch,
  Checkbox,
  Alert,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import StarIcon from "@mui/icons-material/Star";
import ImageUploader from "./ImageUploader";
import { SuggestedMessagesChips } from "../features/emailMessageForm/components/SuggestedMessagesChips";
import { useDataContext } from "../contexts/DataContext";
import { log, req } from "../utils";
import { validatePulleyCover } from "../utils/pulleyCoverAssessment";

export default function PmMachineAssessment({
  machine,
  job,
  onAssessmentComplete,
  onClose,
  open
}) {
  const { technicians, machines } = useDataContext();
  const current_technician = localStorage.getItem("_bc_tech_user_id");
  const [nbrImagesAdded, setNbrImagesAdded] = useState(false);
  const [nbrPulleyImagesAdded, setNbrPulleyImagesAdded] = useState(false);
  const [suggestedMessages, setSuggestedMessages] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const messageRef = useRef(null);

  // PM Assessment state - simplified version of the job completion assessment
  const [state, setState] = useState({
    machine_id: machine?.machine_id_pk || "",
    machine_number: machine?.serial_id || "",
    venue_id_fk: job?.service_venue_id || "",
    service_call_id_fk: job?.service_call_id || "",
    machine_rating: 0,
    bottle_count: 0,
    previous_months_bottle_count: 0,
    latitude: job?.location?.latitude || 0,
    longitude: job?.location?.longitude || 0,
    accuracy: job?.location?.accuracy || 0,
    machine_inspection_completed: false,
    machine_cleaned: false,
    completion_notes: "",
    images: [],
    machine_tagged: true,
    machine_tagged_date: "",
    machine_tag: "",
    machine_tag_due_date: "",
    machine_tagged_by: current_technician || "",
    machine_stable: false,
    bin_full_height: false,
    fan_working: false,
    amp_reading: "",
    chisel_photo: null,
    pulley_cover_checked: false,
    pulley_cover_photo: null,
    pm_completed: true, // This is a PM assessment
  });

  useEffect(() => {
    setSuggestedMessages([
      "PM completed successfully.",
      "Machine running smoothly.",
      "Minor issues noted.",
      "Requires follow-up.",
      "All systems operational.",
    ]);
  }, []);

  useEffect(() => {
    if (machine?.serial_id) {
      setState(prev => ({
        ...prev,
        machine_number: machine.serial_id,
        machine_id: machine.machine_id_pk
      }));
    }
  }, [machine]);

  const handleStateChange = (key, value) => {
    setState((prevState) => ({
      ...prevState,
      [key]: value,
    }));
  };

  const handleSuggestionClick = (suggestion) => {
    try {
      messageRef.current.value += ` ${suggestion}`;
      handleStateChange("completion_notes", suggestion);
    } catch (error) {
      log("handleSuggestionClick", error);
    }
  };

  const handleUpload = (file) => {
    try {
      if (Array.isArray(file) && file.length === 1) {
        file = file[0];
      }
      let images = [...state.images, file];
      handleStateChange("images", images);
      setNbrImagesAdded(true);
      console.log("file uploaded", file);
    } catch (error) {
      log("handleUpload", error);
    }
  };

  const imagesFailed = (error) => {
    try {
      let images = [...state.images, 'error uploading image'];
      handleStateChange("images", images);
      log("imagesFailed", error);
    } catch (error) {
      log("imagesFailed", error);
    }
  };

  const handlePulleyCoverUpload = (file) => {
    try {
      if (Array.isArray(file) && file.length === 1) {
        file = file[0];
      }
      handleStateChange("pulley_cover_photo", file);
      setNbrPulleyImagesAdded(true);
    } catch (error) {
      log("handlePulleyCoverUpload", error);
    }
  };

  const pulleyCoverPhotoFailed = (error) => {
    try {
      handleStateChange("pulley_cover_photo", "error uploading image");
      log("pulleyCoverPhotoFailed", error);
    } catch (err) {
      log("pulleyCoverPhotoFailed", err);
    }
  };

  const handleSubmit = async () => {
    const serial = machine?.serial_id || "unknown";
    const pulleyError = validatePulleyCover(state, serial);
    if (pulleyError) {
      alert(pulleyError);
      return;
    }

    setIsSubmitting(true);
    
    try {
      // Post the PM assessment data
      const response = await req("POST", "record_pm_assessment", {
        service_call_id: job.service_call_id,
        machine_id: machine.machine_id_pk,
        assessment_data: JSON.stringify(state)
      });

      // Check if response has the expected structure
      if (response && response.success === true) {
        // Call the success callback with the response data
        onAssessmentComplete(machine, state, response);
        onClose();
      } else {
        // Handle API error response
        const errorMessage = response?.message || 'Failed to submit PM assessment';
        onAssessmentComplete(machine, state, { success: false, message: errorMessage });
        onClose();
      }
    } catch (error) {
      console.error('Error submitting PM assessment:', error);
      log("handleSubmit PM Assessment", error);
      // Handle network/request errors
      onAssessmentComplete(machine, state, { success: false, message: 'Network error occurred' });
      onClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>
        PM Assessment - {machine?.serial_id || 'Machine'}
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              Machine Assessment for {machine?.serial_id}
            </Typography>
          </Grid>

          {/* Machine Rating */}
          <Grid item xs={6}>
            <Typography variant="body2">Machine Rating</Typography>
          </Grid>
          <Grid item xs={6}>
            <Rating
              name="machine-rating"
              value={state.machine_rating}
              precision={0.5}
              onChange={(event, newValue) => {
                handleStateChange("machine_rating", newValue);
              }}
              sx={{
                "& .MuiRating-iconFilled": { color: "persiangreen.main" },
                "& .MuiRating-iconHover": { color: "persiangreen.dark" },
                "&& .MuiSvgIcon-root": { color: "inherit" },
              }}
              emptyIcon={
                <StarIcon style={{ opacity: 0.55 }} fontSize="inherit" />
              }
            />
          </Grid>

          {/* Bottle Count Fields */}
          <Grid item xs={6}>
            <TextField
              id="bottleCount"
              name="bottleCount"
              label="Bottle Count"
              size="small"
              type="number"
              fullWidth
              onChange={(e) =>
                handleStateChange("bottle_count", e.target.value)
              }
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              id="previousBottleCount"
              name="previousBottleCount"
              label="Previous Month's Bottle Count"
              size="small"
              type="number"
              fullWidth
              onChange={(e) =>
                handleStateChange("previous_months_bottle_count", e.target.value)
              }
            />
          </Grid>

          {/* Machine Status Checkboxes */}
          <Grid item xs={4}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={state.fan_working}
                  onChange={(event) =>
                    handleStateChange("fan_working", event.target.checked)
                  }
                  color="persiangreen"
                />
              }
              label="Fan Working"
            />
          </Grid>
          <Grid item xs={4}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={state.machine_inspection_completed}
                  onChange={(event) =>
                    handleStateChange("machine_inspection_completed", event.target.checked)
                  }
                  color="persiangreen"
                />
              }
              label="Inspected"
            />
          </Grid>
          <Grid item xs={4}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={state.machine_cleaned}
                  onChange={(event) =>
                    handleStateChange("machine_cleaned", event.target.checked)
                  }
                  color="persiangreen"
                />
              }
              label="Cleaned"
            />
          </Grid>

          {/* Machine Number */}
          <Grid item xs={12}>
            <TextField
              label="Machine Number"
              variant="outlined"
              fullWidth
              margin="normal"
              value={state.machine_number || ''}
              onChange={(event) => handleStateChange('machine_number', event.target.value)}
            />
          </Grid>

          {/* Tag Due Date */}
          <Grid item xs={6}>
            <TextField
              label="Tag Due Date"
              type="date"
              value={state.machine_tag_due_date || ''}
              onChange={(event) => handleStateChange('machine_tag_due_date', event.target.value)}
              InputLabelProps={{
                shrink: true,
              }}
              fullWidth
              variant="outlined"
              margin="normal"
            />
          </Grid>

          {/* Amp Reading */}
          <Grid item xs={6}>
            <TextField
              label="Amp Reading"
              variant="outlined"
              fullWidth
              margin="normal"
              value={state.amp_reading || ''}
              onChange={(event) => handleStateChange('amp_reading', event.target.value)}
            />
          </Grid>

          {/* Additional Status Checkboxes */}
          <Grid item xs={4}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={state.machine_stable}
                  onChange={(event) =>
                    handleStateChange("machine_stable", event.target.checked)
                  }
                  color="persiangreen"
                />
              }
              label="Machine Stable"
            />
          </Grid>
          <Grid item xs={4}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={state.bin_full_height}
                  onChange={(event) =>
                    handleStateChange("bin_full_height", event.target.checked)
                  }
                  color="persiangreen"
                />
              }
              label="Bin Full Height"
            />
          </Grid>
          <Grid item xs={4}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={state.machine_tagged}
                  onChange={(event) =>
                    handleStateChange("machine_tagged", event.target.checked)
                  }
                  color="persiangreen"
                />
              }
              label="Machine Tagged"
            />
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  required
                  checked={state.pulley_cover_checked}
                  onChange={(event) =>
                    handleStateChange("pulley_cover_checked", event.target.checked)
                  }
                  color="persiangreen"
                />
              }
              label="Pulley Cover Checked (required)"
            />
          </Grid>

          <Grid item xs={12}>
            {nbrPulleyImagesAdded && (
              <Alert sx={{ marginBottom: ".5rem;" }} severity="success">
                Pulley cover photo added
              </Alert>
            )}
            <ImageUploader
              endpointUrl="upload"
              primaryUse="service_calls"
              primaryUseKey={job.service_call_id}
              otherFormData={[{ name: "photo_type", value: "pulley_cover" }]}
              successCallback={handlePulleyCoverUpload}
              failureCallback={pulleyCoverPhotoFailed}
              uploadLabel="Pulley Cover Photo (required)"
            />
          </Grid>

          {/* Image Upload */}
          <Grid item xs={12}>
            {nbrImagesAdded && (
              <Alert sx={{ marginBottom: ".5rem;" }} severity="success">
                Image(s) added
              </Alert>
            )}
            <ImageUploader
              endpointUrl="upload"
              primaryUse="service_calls"
              primaryUseKey={job.service_call_id}
              otherFormData={null}
              successCallback={handleUpload}
              failureCallback={imagesFailed}
              uploadLabel="PM Photo"
            />
          </Grid>

          {/* Notes */}
          <Grid item xs={12}>
            {suggestedMessages && suggestedMessages.length > 0 && (
              <SuggestedMessagesChips
                suggestedMessages={suggestedMessages}
                handleSuggestionClick={handleSuggestionClick}
              />
            )}
            <TextField
              id="pmNotes"
              name="pmNotes"
              multiline
              placeholder="PM notes and observations..."
              fullWidth
              rows={3}
              color="persiangreen"
              onChange={(e) =>
                handleStateChange("completion_notes", e.target.value)
              }
              inputRef={messageRef}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary">
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          color="persiangreen"
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Submitting Assessment...' : 'Complete PM Assessment'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
